/**
 * Issue #1318 (Pick XI coach 3 of 3; D297, D300, D302) — the pure half of Game
 * plan & notes, step 2 of 2: the form's shape, what it opens as, the body
 * `PUT /plan` and `POST /publish` take, and the lines the team summary prints.
 * No React and no network.
 */

/** The three toss calls the API takes, in the order the board draws them. */
export const TOSS_CHOICES = [
  { value: "bat", label: "Bat", testId: "plan-toss-bat" },
  { value: "bowl", label: "Bowl", testId: "plan-toss-bowl" },
  { value: "on_the_day", label: "On the day", testId: "plan-toss-day" },
];

/** A plan with nothing in it. Everything on the screen is optional, and Players see the game plan starts ticked (the API's default). */
export const EMPTY_PLAN = {
  toss: null,
  remember: ["", "", ""],
  planBat: "",
  planBall: "",
  planOpposition: "",
  arriveBy: "",
  teamNotes: "",
  playersSeePlan: true,
};

/** The form a `GET /api/coach-games/:id` payload opens as: the draft plan when there is one, otherwise empty. */
export function planFromServer(game) {
  const d = game?.plans?.draft;
  if (!d) return { ...EMPTY_PLAN, remember: [...EMPTY_PLAN.remember] };
  const remember = Array.isArray(d.remember) ? d.remember : [];
  return {
    toss: d.toss || null,
    remember: [0, 1, 2].map((i) => remember[i] || ""),
    planBat: d.plan_bat || "",
    planBall: d.plan_ball || "",
    planOpposition: d.plan_opposition || "",
    arriveBy: d.arrive_by || "",
    teamNotes: d.team_notes || "",
    playersSeePlan: d.players_see_plan !== false,
  };
}

/** The body `PUT /plan` and `POST /publish` share. A blank text goes as null. */
export function planBody(form) {
  const text = (v) => {
    const t = (v || "").trim();
    return t ? t : null;
  };
  return {
    toss: form.toss,
    remember: form.remember.map(text),
    plan_bat: text(form.planBat),
    plan_ball: text(form.planBall),
    plan_opposition: text(form.planOpposition),
    arrive_by: form.arriveBy || null,
    team_notes: text(form.teamNotes),
    players_see_plan: !!form.playersSeePlan,
  };
}

/**
 * The draft sheet as the summary prints it: the XI in batting order with tags,
 * the 12th man, the two opening bowlers and the volunteers line. Null when
 * there is no draft sheet. `names` maps a player id to a name.
 */
export function teamSummary(game) {
  const draft = game?.sheets?.draft;
  if (!draft) return null;
  const name = Object.fromEntries((game.squad || []).map((m) => [m.player_id, m.full_name]));
  const xi = [...(draft.xi || [])].sort((a, b) => (a.bat_order ?? 99) - (b.bat_order ?? 99));
  const nameOf = (e) => name[e.player_id] || e.full_name || "";
  const rows = xi.map((e, i) => ({ n: i + 1, name: nameOf(e), captain: !!e.captain, vice: !!e.vice_captain, keeper: !!e.keeper }));
  const openers = xi
    .filter((e) => e.bowl_order === 1 || e.bowl_order === 2)
    .sort((a, b) => a.bowl_order - b.bowl_order)
    .map(nameOf);
  return {
    rows,
    twelfth: draft.twelfth ? name[draft.twelfth.player_id] || draft.twelfth.full_name || "" : null,
    openers,
    volunteers: volunteersLine(game.volunteers),
    needsCheck: !(xi.some((e) => e.captain) && xi.some((e) => e.keeper)),
  };
}

/** "[SCORER] scoring · [UMPIRE 1], [UMPIRE 2] umpiring", built only from the names that are there. Empty when there are none. */
export function volunteersLine(v) {
  const clean = (s) => (s || "").trim();
  const parts = [];
  if (clean(v?.scorer)) parts.push(`${clean(v.scorer)} scoring`);
  const umpires = [clean(v?.umpire_1), clean(v?.umpire_2)].filter(Boolean);
  if (umpires.length) parts.push(`${umpires.join(", ")} umpiring`);
  return parts.join(" · ");
}
